'use client';
import { PublicHeroImage } from '@/components/public/public-hero-image';
import heroStyles from '@/components/public/public-hero.module.css';

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SaveAccountItemButton } from '@/components/account/save-account-item-button';
import { usePublicBlogPost } from '@/hooks/use-public-blog';
import { useHomepageSettings } from '@/hooks/use-homepage-settings';
import { getUploadedImage } from '@/lib/book-image';
import { UNTANGLED_BLOG_PATHWAYS } from '@/lib/content/untangled-blog';
import { formatAsThisYear } from '@/lib/date';
import { ROUTES } from '@/lib/routes';
import type { PlatformBlogPostDoc } from '@/types/platform';

function estimateReadingMinutes(post: Pick<PlatformBlogPostDoc, 'body' | 'excerpt'>) {
  const text = `${post.body || ''} ${post.excerpt || ''}`.trim();
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  return Math.max(2, Math.ceil(wordCount / 220));
}

function formatDateString(value?: unknown) {
  const clean = String(value || '').trim();
  if (!clean) return '';
  const parsed = Date.parse(clean.length === 10 ? `${clean}T00:00:00` : clean);
  if (!Number.isFinite(parsed)) return clean;
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(parsed));
}

function timestampDateLabel(value?: unknown) {
  if (!value) return '';
  if (typeof value === 'object') {
    const timestamp = value as { toMillis?: () => number; seconds?: number };
    const millis = typeof timestamp.toMillis === 'function' ? timestamp.toMillis() : typeof timestamp.seconds === 'number' ? timestamp.seconds * 1000 : 0;
    if (millis) return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(millis));
  }
  return formatDateString(value);
}

function postDateLabel(post: PlatformBlogPostDoc) {
  return formatDateString(post.publishedDate) || timestampDateLabel(post.publishedAt ?? post.updatedAt ?? post.createdAt) || formatAsThisYear(String(post.publishedAt ?? post.updatedAt ?? post.createdAt ?? ''));
}

function displayReadTime(post: PlatformBlogPostDoc) {
  return Math.max(1, Number(post.readingMinutes || estimateReadingMinutes(post)));
}

function EmptyImageSurface({ className = '' }: { className?: string }) {
  return <div className={`h-full w-full bg-[radial-gradient(circle_at_18%_16%,rgba(210,181,151,.42),transparent_30%),linear-gradient(135deg,#f7efe5,#eee1d3)] ${className}`} />;
}

function PostImage({
  post,
  className = 'h-full w-full object-cover object-center',
  priority = false,
}: {
  post: Pick<PlatformBlogPostDoc, 'coverImageUrl' | 'coverImageAlt' | 'title'>;
  className?: string;
  priority?: boolean;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const uploaded = getUploadedImage(post.coverImageUrl, post.coverImageAlt, post.title);

  if (uploaded && uploaded.src !== failedSrc) {
    return (
      <Image
        src={uploaded.src}
        alt={uploaded.alt}
        width={1600}
        height={1000}
        className={className}
        priority={priority}
        onError={() => setFailedSrc(uploaded.src)}
      />
    );
  }

  return <EmptyImageSurface />;
}

function OptionalPostImageFrame({
  post,
  frameClassName,
  imageClassName = 'h-full w-full object-cover object-center',
}: {
  post: Pick<PlatformBlogPostDoc, 'coverImageUrl' | 'coverImageAlt' | 'title'>;
  frameClassName: string;
  imageClassName?: string;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const uploaded = getUploadedImage(post.coverImageUrl, post.coverImageAlt, post.title);

  if (!uploaded || uploaded.src === failedSrc) return null;

  return (
    <div className={frameClassName}>
      <Image src={uploaded.src} alt={uploaded.alt} width={900} height={600} className={imageClassName} onError={() => setFailedSrc(uploaded.src)} />
    </div>
  );
}

function CoverImageFill({ post, priority = false }: { post: Pick<PlatformBlogPostDoc, 'coverImageUrl' | 'coverImageAlt' | 'title'>; priority?: boolean }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const uploaded = getUploadedImage(post.coverImageUrl, post.coverImageAlt, post.title);

  if (uploaded && uploaded.src !== failedSrc) {
    return <Image src={uploaded.src} alt={uploaded.alt} fill sizes="100vw" priority={priority} className="object-cover object-center" onError={() => setFailedSrc(uploaded.src)} />;
  }

  return <EmptyImageSurface />;
}

function AuthorAvatar({ post }: { post: PlatformBlogPostDoc }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const uploaded = getUploadedImage(post.authorAvatarUrl, post.authorAvatarAlt, post.authorName || post.title);
  const initials = (post.authorName || 'Author')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'A';

  if (uploaded && uploaded.src !== failedSrc) {
    return <Image src={uploaded.src} alt={uploaded.alt} width={88} height={88} className="h-16 w-16 rounded-full object-cover" onError={() => setFailedSrc(uploaded.src)} />;
  }

  return <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ead7c8] font-headline text-xl text-[#7c4f3e]">{initials}</div>;
}

function ArticleImage({ image, compact = false }: { image: NonNullable<PlatformBlogPostDoc['contentImages']>[number]; compact?: boolean }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const uploaded = getUploadedImage(image.imageUrl, image.imageAlt, 'Blog image');
  if (!uploaded || uploaded.src === failedSrc) return null;

  return (
    <figure className={compact ? 'm-0' : 'my-10'}>
      <div className={`${compact ? 'aspect-[4/3] rounded-[1.25rem]' : 'aspect-[16/6.5] rounded-[.75rem]'} overflow-hidden`}>
        <Image
          src={uploaded.src}
          alt={uploaded.alt}
          width={compact ? 700 : 1400}
          height={compact ? 520 : 620}
          className="h-full w-full object-cover object-center"
          onError={() => setFailedSrc(uploaded.src)}
        />
      </div>
      {image.caption ? <figcaption className="mt-3 text-sm leading-6 text-[#6f6259]">{image.caption}</figcaption> : null}
    </figure>
  );
}

function hasRenderableImage(image?: NonNullable<PlatformBlogPostDoc['contentImages']>[number] | null) {
  return Boolean(image?.imageUrl && getUploadedImage(image.imageUrl, image.imageAlt, 'Blog image'));
}

function pathwayForPost(post: PlatformBlogPostDoc) {
  return UNTANGLED_BLOG_PATHWAYS.find((pathway) => post.tags?.includes(pathway.id) || post.tags?.includes(pathway.label));
}

function visibleCategory(post: PlatformBlogPostDoc) {
  return post.category || pathwayForPost(post)?.label || post.tags?.[0] || 'Editorial';
}

function visibleTags(post: PlatformBlogPostDoc) {
  const category = visibleCategory(post);
  const tags = [category, ...(post.tags ?? [])]
    .map((tag) => tag.trim())
    .filter(Boolean)
    .filter((tag) => !UNTANGLED_BLOG_PATHWAYS.some((pathway) => pathway.id === tag));
  return Array.from(new Set(tags)).slice(0, 8);
}

function BlogNewsletterBox({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState('');
  const [leadMessage, setLeadMessage] = useState<string | null>(null);
  const [leadError, setLeadError] = useState<string | null>(null);
  const [leadWorking, setLeadWorking] = useState(false);

  async function handleLeadSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLeadMessage(null);
    setLeadError(null);
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setLeadError('Enter an email address.');
      return;
    }
    try {
      setLeadWorking(true);
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: cleanEmail,
          consent: true,
          sourceSlug: 'blog',
          sourceType: 'manual',
          sourceName: 'The Untangled Blog',
          offer: 'blog-newsletter',
          redirectPath: '/blog',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to subscribe.');
      setEmail('');
      setLeadMessage(data.message || 'Request recorded. Email confirmation is required.');
    } catch (err) {
      setLeadError(err instanceof Error ? err.message : 'Unable to subscribe.');
    } finally {
      setLeadWorking(false);
    }
  }

  return (
    <form onSubmit={handleLeadSubmit} className={`rounded-[1.35rem] border border-[#e7d5c7] bg-[#f6ddd2] ${compact ? 'p-6' : 'p-7'}`}>
      <h3 className="font-headline text-2xl leading-tight text-[#2f2d2a]">Get new essays in your inbox</h3>
      <p className="mt-3 text-sm leading-7 text-[#5e554d]">Practical reflections from The Untangled Life, delivered when new essays publish.</p>
      <input id="blog-newsletter-email" name="email" value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required placeholder="Your email address" className="mt-5 h-12 w-full rounded-xl border border-white/80 bg-white px-4 text-sm text-[#2f2d2a] outline-none placeholder:text-[#877a70] focus:border-[#5f8769]" />
      <Button type="submit" disabled={leadWorking} className="mt-4 rounded-lg bg-[#5f8769] text-white hover:bg-[#547a5f]">{leadWorking ? 'Subscribing...' : 'Subscribe'}</Button>
      {leadMessage ? <p className="mt-3 text-xs text-[#315d3f]">{leadMessage}</p> : null}
      {leadError ? <p className="mt-3 text-xs text-[#8a3d2d]">{leadError}</p> : null}
      <p className="mt-4 text-xs text-[#6f6259]">No spam. <Link href="/newsletter" className="underline">Manage newsletter preferences</Link>.</p>
    </form>
  );
}

function BlogCard({ post }: { post: PlatformBlogPostDoc }) {
  return (
    <Link href={`/blog/${post.slug}`} className="group block h-full">
      <article className="flex h-full flex-col overflow-hidden rounded-[1.35rem] border border-[#e7d5c7] bg-white shadow-[0_18px_50px_rgba(70,49,35,0.06)] transition duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_24px_70px_rgba(70,49,35,0.12)]">
        <div className="aspect-[1.22/1] overflow-hidden bg-[#f3eadf]">
          <PostImage post={post} className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-105" />
        </div>
        <div className="flex flex-1 flex-col p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#b8735c]">{visibleCategory(post)}</p>
          <h3 className="mt-3 font-headline text-xl leading-snug text-[#2f2d2a]">{post.title}</h3>
          <p className="mt-3 line-clamp-3 text-sm leading-6 text-[#665d55]">{post.excerpt}</p>
          <div className="mt-auto pt-5 text-xs text-[#6f6259]">
            <span>{displayReadTime(post)} min read</span>
            <span className="px-2">-</span>
            <span>{postDateLabel(post)}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}

function ArticleSuggestionCard({ post }: { post: PlatformBlogPostDoc }) {
  return (
    <Link href={`/blog/${post.slug}`} className="group block h-full">
      <article className="flex h-full flex-col overflow-hidden rounded-[.65rem] border border-[#e5d1c3] bg-white shadow-[0_16px_45px_rgba(70,49,35,.05)] transition group-hover:-translate-y-0.5">
        <OptionalPostImageFrame post={post} frameClassName="aspect-[1.75/1] overflow-hidden bg-[#f1e5d8]" imageClassName="h-full w-full object-cover object-center transition duration-500 group-hover:scale-105" />
        <div className="flex flex-1 flex-col px-4 py-4">
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#b8735c]">{visibleCategory(post)}</p>
          <h3 className="mt-2 font-headline text-[1.02rem] leading-snug text-[#2f2d2a]">{post.title}</h3>
          <div className="mt-auto pt-4 text-[11px] text-[#6f6259]">
            <span>{displayReadTime(post)} min read</span>
            <span className="px-2">-</span>
            <span>{postDateLabel(post)}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}

function PopularRead({ post }: { post: PlatformBlogPostDoc }) {
  const hasImage = Boolean(getUploadedImage(post.coverImageUrl, post.coverImageAlt, post.title));
  return (
    <Link href={`/blog/${post.slug}`} className={`group grid gap-3 rounded-2xl p-2 transition hover:bg-[#f8efe8] ${hasImage ? 'grid-cols-[58px_minmax(0,1fr)]' : 'grid-cols-1'}`}>
      {hasImage ? <OptionalPostImageFrame post={post} frameClassName="aspect-square overflow-hidden rounded-xl bg-[#f3eadf]" /> : null}
      <div className="min-w-0">
        <p className="line-clamp-2 font-headline text-sm leading-snug text-[#2f2d2a] group-hover:text-[#7c4f3e]">{post.title}</p>
        <p className="mt-1 text-xs text-[#6f6259]">{displayReadTime(post)} min read</p>
      </div>
    </Link>
  );
}

export function BlogIndexPanel({ initialPosts = [], initialNextCursor = null }: { initialPosts?: PlatformBlogPostDoc[]; initialNextCursor?: string | null }) {
  const [posts, setPosts] = useState(initialPosts);
  const [nextCursor, setNextCursor] = useState(initialNextCursor);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { settings } = useHomepageSettings();
  const [query, setQuery] = useState('');
  const [activeTopic, setActiveTopic] = useState('all');
  const [visibleCount, setVisibleCount] = useState(6);
  const blogHeroImage = getUploadedImage(settings?.blogHeroImageUrl, settings?.blogHeroImageAlt, 'The Untangled Blog hero image');

  const topics = useMemo(() => {
    const values = new Map<string, string>();
    for (const post of posts) {
      const category = visibleCategory(post).trim();
      if (category) values.set(category.toLowerCase(), category);
      for (const tag of post.tags ?? []) {
        if (!tag || UNTANGLED_BLOG_PATHWAYS.some((pathway) => pathway.id === tag)) continue;
        values.set(tag.toLowerCase(), tag);
      }
    }
    return Array.from(values.values()).slice(0, 10);
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const search = query.trim().toLowerCase();
    return posts.filter((post) => {
      const matchesTopic = activeTopic === 'all' || visibleCategory(post).toLowerCase() === activeTopic || post.tags?.some((tag) => tag.toLowerCase() === activeTopic);
      const haystack = [post.title, post.excerpt, post.metaDescription, post.category, post.authorName, ...(post.tags ?? [])].join(' ').toLowerCase();
      return matchesTopic && (!search || haystack.includes(search));
    });
  }, [activeTopic, posts, query]);

  const featuredArticle = filteredPosts.find((post) => post.isFeatured) ?? filteredPosts[0] ?? null;
  const latestArticles = filteredPosts.filter((post) => post.slug !== featuredArticle?.slug);
  const visibleArticles = latestArticles.slice(0, visibleCount);
  const popularReads = [...posts].sort((a, b) => Number(Boolean(b.isFeatured)) - Number(Boolean(a.isFeatured))).slice(0, 3);

  async function loadMoreArticles() {
    if (loading) return;
    if (latestArticles.length > visibleCount) { setVisibleCount((count) => count + 6); return; }
    if (!nextCursor) return;
    setLoading(true); setError('');
    try {
      const response = await fetch(`/api/public/blog?after=${encodeURIComponent(nextCursor)}`);
      const page = await response.json() as { posts?: PlatformBlogPostDoc[]; nextCursor?: string | null; error?: string };
      if (!response.ok || !page.posts) throw new Error(page.error || 'Unable to load more articles.');
      setPosts((current) => [...current, ...page.posts!.filter((post) => !current.some((item) => item.postId === post.postId))]);
      setNextCursor(page.nextCursor || null); setVisibleCount((count) => count + 12);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to load more articles.'); }
    finally { setLoading(false); }
  }

  return (
    <div className="bg-[#fbf8f3] text-[#2f2d2a]">
      <section className={`${heroStyles.frame} border-b border-[#e7d5c7]`} data-public-hero>
        <div className={heroStyles.backdrop} data-scenery-hero>
          {blogHeroImage ? <PublicHeroImage src={blogHeroImage.src} alt={blogHeroImage.alt} fallback={<EmptyImageSurface />} /> : <EmptyImageSurface />}
          <div className={heroStyles.overlay} />
        </div>
        <div className={`${heroStyles.content} flex`}>
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.42em] text-[#4c4b45]">The Untangled Blog</p>
            <h1 className="mt-6 font-headline text-5xl leading-[.98] tracking-tight text-[#2f2d2a] sm:text-6xl lg:text-7xl">Clear language for complicated emotional patterns.</h1>
            <p className="mt-7 max-w-xl text-lg leading-8 text-[#4d4943]">Thoughtful essays on boundaries, healing, self-trust, clarity, and the patterns that shape how we love.</p>
            <div className="mt-9 flex max-w-xl flex-col gap-3 sm:flex-row">
              <input id="blog-search" name="blog-search" type="search" autoComplete="off" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search articles..." className="h-14 flex-1 rounded-xl border border-white/70 bg-white/94 px-5 text-sm text-[#2f2d2a] shadow-[0_12px_35px_rgba(70,49,35,0.08)] outline-none placeholder:text-[#877a70] focus:border-[#6f8b69]" />
              <Button type="button" className="h-14 rounded-xl bg-[#547a5f] px-7 text-white hover:bg-[#496d53]" onClick={() => setVisibleCount(Math.max(6, visibleCount))}>Browse articles</Button>
            </div>
            {loading ? <p className="mt-4 text-sm text-[#6f6259]">Loading articles...</p> : null}
            {nextCursor && (query || activeTopic !== 'all') ? <p className="mt-4 text-sm text-[#6f6259]">Filtering the articles loaded so far. Load more below to include older articles.</p> : null}
            {error ? <p role="alert" className="mt-4 text-sm text-[#8a5a45]">{error}</p> : null}
          </div>
        </div>
      </section>

      <main className="container mx-auto w-full max-w-[1340px] px-5 py-10 sm:px-8 xl:px-10">
        {featuredArticle ? (
          <section className="overflow-hidden rounded-[1.75rem] border border-[#e7d5c7] bg-white shadow-[0_24px_90px_rgba(70,49,35,0.08)]">
            <div className="grid gap-0 lg:grid-cols-[minmax(0,.9fr)_minmax(0,1fr)]">
              <div className="aspect-[1.28/1] overflow-hidden bg-[#f3eadf] lg:aspect-auto"><PostImage post={featuredArticle} className="h-full w-full object-cover object-center" priority /></div>
              <div className="flex flex-col justify-center p-8 sm:p-12">
                <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#b8735c]">Featured article</p>
                <h2 className="mt-6 font-headline text-4xl leading-tight text-[#2f2d2a]">{featuredArticle.title}</h2>
                <p className="mt-4 text-sm text-[#6f6259]"><span>{visibleCategory(featuredArticle)}</span><span className="px-3">-</span><span>{displayReadTime(featuredArticle)} min read</span><span className="px-3">-</span><span>{postDateLabel(featuredArticle)}</span></p>
                <p className="mt-6 max-w-xl text-base leading-8 text-[#5e554d]">{featuredArticle.excerpt}</p>
                <div className="mt-8"><Button asChild className="rounded-lg bg-[#5f8769] px-6 text-white hover:bg-[#547a5f]"><Link href={`/blog/${featuredArticle.slug}`}>Read the article</Link></Button></div>
              </div>
            </div>
          </section>
        ) : <Card className="rounded-[1.75rem] border border-[#e7d5c7] bg-white shadow-sm"><CardContent className="p-8 text-sm text-[#6f6259]">No published blog posts are available yet.</CardContent></Card>}

        {topics.length ? (
          <section className="mt-10">
            <h2 className="font-headline text-2xl text-[#2f2d2a]">Explore topics</h2>
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={() => setActiveTopic('all')} className={`rounded-2xl border px-6 py-3 text-sm font-semibold transition ${activeTopic === 'all' ? 'border-[#5f8769] bg-[#5f8769] text-white' : 'border-[#e7d5c7] bg-[#fbf4ee] text-[#5e554d] hover:border-[#b8735c]'}`}>All</button>
              {topics.map((topic) => <button key={topic} type="button" onClick={() => setActiveTopic(topic.toLowerCase())} className={`rounded-2xl border px-6 py-3 text-sm font-semibold transition ${activeTopic === topic.toLowerCase() ? 'border-[#5f8769] bg-[#5f8769] text-white' : 'border-[#e7d5c7] bg-[#fbf4ee] text-[#5e554d] hover:border-[#b8735c]'}`}>{topic}</button>)}
            </div>
          </section>
        ) : null}

        <section className="mt-12 grid gap-9 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          <div>
            <h2 className="font-headline text-2xl text-[#2f2d2a]">Latest articles</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{visibleArticles.map((post) => <BlogCard key={post.postId} post={post} />)}</div>
            {!loading && !visibleArticles.length ? <Card className="mt-6 rounded-[1.5rem] border border-[#e7d5c7] bg-white"><CardContent className="p-6 text-sm text-[#6f6259]">No articles match that search.</CardContent></Card> : null}
            {latestArticles.length > visibleCount || nextCursor ? <div className="mt-8 flex justify-center"><Button type="button" disabled={loading} className="rounded-lg bg-[#5f8769] px-12 text-white hover:bg-[#547a5f]" onClick={() => void loadMoreArticles()}>{loading ? 'Loading…' : 'Load more articles'}</Button></div> : null}
          </div>
          <aside className="space-y-6 lg:sticky lg:top-24">
            <div className="rounded-[1.5rem] border border-[#e7d5c7] bg-white p-6 shadow-[0_18px_50px_rgba(70,49,35,0.06)]"><h3 className="font-headline text-2xl text-[#2f2d2a]">Popular reads</h3><div className="mt-5 space-y-2">{popularReads.map((post) => <PopularRead key={`popular-${post.postId}`} post={post} />)}</div></div>
            <div className="rounded-[1.5rem] border border-[#e7d5c7] bg-[#eef0e6] p-6"><h3 className="font-headline text-2xl text-[#2f2d2a]">About this blog</h3><p className="mt-3 text-sm leading-7 text-[#5e554d]">The Untangled Blog helps readers name emotional patterns with clarity and compassion, then return to practical next steps.</p><Button asChild variant="outline" className="mt-5 border-[#b8735c] bg-transparent text-[#8a5a45] hover:bg-white/50"><Link href={ROUTES.about}>More about the blog</Link></Button></div>
            <BlogNewsletterBox compact />
          </aside>
        </section>
      </main>
    </div>
  );
}

type TextBlock = { type: 'paragraph'; text: string } | { type: 'list'; items: string[] };
type BlogContentBlock = NonNullable<PlatformBlogPostDoc['contentBlocks']>[number];

function textBlocks(lines: string[]) {
  const blocks: TextBlock[] = [];
  let listItems: string[] = [];
  for (const line of lines) {
    const listMatch = line.match(/^[-*]\s+(.+)/);
    if (listMatch) {
      listItems.push(listMatch[1]);
      continue;
    }
    if (listItems.length) {
      blocks.push({ type: 'list', items: listItems });
      listItems = [];
    }
    blocks.push({ type: 'paragraph', text: line });
  }
  if (listItems.length) blocks.push({ type: 'list', items: listItems });
  return blocks;
}

function ArticleSection({ section, dropCap = false }: { section: string; dropCap?: boolean }) {
  const lines = section.split('\n').map((line) => normalizeArticleText(line.trim())).filter(Boolean);
  if (!lines.length) return null;
  const firstLine = lines[0];
  const headingMatch = firstLine.match(/^#{2,3}\s+(.+)/);
  const hasHeading = Boolean(headingMatch);
  const heading = headingMatch?.[1] ?? null;
  const bodyLines = hasHeading ? lines.slice(1) : lines;
  const blocks = textBlocks(bodyLines);
  const firstParagraphIndex = dropCap ? blocks.findIndex((block) => block.type === 'paragraph') : -1;

  return (
    <section className="space-y-3">
      {heading ? <h2 className="font-headline text-[1.75rem] leading-tight text-[#2f2d2a]">{heading}</h2> : null}
      {blocks.map((block, index) => {
        if (block.type === 'list') {
          return <ul key={`list-${index}`} className="ml-5 list-disc space-y-1 font-serif text-[16px] leading-[1.66] marker:text-[#b8735c]">{block.items.map((item) => <li key={item}>{item}</li>)}</ul>;
        }
        const applyDropCap = dropCap && index === firstParagraphIndex;
        return applyDropCap ? <DropCapParagraph key={`p-${index}`} text={block.text} /> : <ArticleParagraph key={`p-${index}`}>{block.text}</ArticleParagraph>;
      })}
    </section>
  );
}

function ArticleParagraph({ children }: { children: ReactNode }) {
  return <p className="font-serif text-[16px] leading-[1.66] text-[#2f2d2a]">{children}</p>;
}

function DropCapParagraph({ text }: { text: string }) {
  const clean = normalizeArticleText(text.trim());
  if (!clean) return null;
  return (
    <p className="font-serif text-[16px] leading-[1.66] text-[#2f2d2a]">
      <span aria-hidden className="float-left mr-3 mt-[0.16rem] font-headline text-[5.35rem] leading-[.76] text-[#b8735c]">{clean.charAt(0)}</span>
      <span className="sr-only">{clean.charAt(0)}</span>
      {clean.slice(1)}
    </p>
  );
}

function normalizeArticleText(text: string) {
  return text.replace(/([.!?])(?=[A-Z])/g, '$1 ');
}

function PullQuoteBox({ post }: { post: PlatformBlogPostDoc }) {
  if (!post.pullQuote?.trim()) return null;
  return <QuoteBlock quote={post.pullQuote.trim()} attribution={post.pullQuoteAttribution} />;
}

function QuoteBlock({ quote, attribution }: { quote: string; attribution?: string }) {
  if (!quote.trim()) return null;
  return (
    <aside className="grid gap-5 rounded-[1.1rem] bg-[#f7eee7] px-8 py-6 shadow-[inset_0_0_0_1px_rgba(184,115,92,.12)] md:grid-cols-[70px_minmax(0,1fr)] md:items-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/75 text-center font-headline text-5xl leading-none text-[#e0b49e]">&ldquo;</div>
      <div>
        <p className="font-headline text-[1.45rem] italic leading-snug text-[#5a4438]">{quote.trim()}</p>
        {attribution ? <p className="mt-3 text-sm font-semibold text-[#b8735c]">- {attribution}</p> : null}
      </div>
    </aside>
  );
}

function AccentCalloutBox({ post }: { post: PlatformBlogPostDoc }) {
  if (!post.accentCallout?.trim()) return null;
  const tone = post.accentCalloutTone || 'cream';
  const toneClass = tone === 'green'
    ? 'border-[#d7dfcf] bg-[#eef0e6] text-[#425341]'
    : tone === 'rose'
      ? 'border-[#eed2c8] bg-[#f6ddd2] text-[#6b493d]'
      : 'border-[#ead8c8] bg-[#fbf4ee] text-[#5a4438]';
  return (
    <aside className={`rounded-[1.1rem] border px-8 py-6 shadow-[0_18px_50px_rgba(70,49,35,.05)] ${toneClass}`}>
      {post.accentCalloutLabel ? <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.24em] text-[#b8735c]">{post.accentCalloutLabel}</p> : null}
      <p className="text-[17px] leading-8">{post.accentCallout.trim()}</p>
    </aside>
  );
}

function InlineCalloutBox({ text, tone = 'cream' }: { text: string; tone?: 'cream' | 'green' | 'rose' }) {
  if (!text.trim()) return null;
  const toneClass = tone === 'green'
    ? 'border-[#d7dfcf] bg-[#eef0e6] text-[#425341]'
    : tone === 'rose'
      ? 'border-[#eed2c8] bg-[#f6ddd2] text-[#6b493d]'
      : 'border-[#ead8c8] bg-[#f7eee7] text-[#5a4438]';
  return (
    <div className={`grid grid-cols-[42px_minmax(0,1fr)] items-center gap-5 rounded-[.75rem] border px-7 py-5 text-[15px] leading-7 ${toneClass}`}>
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-[#b8735c]"><SmallIcon type="leaf" /></div>
      <p>{text.trim()}</p>
    </div>
  );
}

function ArticleContentBlock({ block, fallbackDropCap = false }: { block: BlogContentBlock; fallbackDropCap?: boolean }) {
  if (block.type === 'quote') {
    return <QuoteBlock quote={block.quote || block.text || ''} attribution={block.attribution} />;
  }
  if (block.type === 'image') {
    if (!block.imageUrl?.trim()) return null;
    return (
      <ArticleImage
        image={{
          imageUrl: block.imageUrl,
          imageAlt: block.imageAlt || 'Blog article image',
          caption: block.caption || '',
          layout: 'full-width',
        }}
      />
    );
  }
  if (block.type === 'callout') {
    return <InlineCalloutBox text={block.text || ''} tone={block.tone || 'cream'} />;
  }
  const parts = [block.heading ? `## ${block.heading}` : '', block.text || ''].filter(Boolean).join('\n');
  return <ArticleSection section={parts} dropCap={Boolean(block.dropCap) || fallbackDropCap} />;
}

function SidebarCard({ title, children }: { title: string; children: ReactNode }) {
  return <section className="rounded-[1.2rem] border border-[#e5d1c3] bg-[#fffdf9]/96 p-6 shadow-[0_18px_55px_rgba(70,49,35,.045)]"><h3 className="font-headline text-[1.35rem] leading-tight text-[#2f2d2a]">{title}</h3><div className="mt-5">{children}</div></section>;
}

function ShareGlyph({ type }: { type: 'heart' | 'bookmark' | 'link' | 'instagram' | 'mail' }) {
  if (type === 'heart') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20.4 5.8c-1.8-1.8-4.7-1.7-6.4.2l-2 2.1-2-2.1C8.3 4.1 5.4 4 3.6 5.8c-1.9 1.9-1.8 5 .2 6.9l8.2 7.5 8.2-7.5c2-1.9 2.1-5 .2-6.9Z" /></svg>;
  if (type === 'bookmark') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M7 4.5h10v15l-5-3-5 3v-15Z" /></svg>;
  if (type === 'link') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10.5 13.5 13.5 10.5" /><path d="M8.8 16.2 7.6 17.4a4 4 0 0 1-5.6-5.6l2.4-2.4A4 4 0 0 1 10 9.4" /><path d="M15.2 7.8 16.4 6.6a4 4 0 0 1 5.6 5.6l-2.4 2.4A4 4 0 0 1 14 14.6" /></svg>;
  if (type === 'instagram') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="5" y="5" width="14" height="14" rx="4" /><circle cx="12" cy="12" r="3.2" /><path d="M16.8 7.4h.1" /></svg>;
  return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 7h16v10H4V7Z" /><path d="m4 8 8 6 8-6" /></svg>;
}

function SmallIcon({ type }: { type: 'category' | 'clock' | 'calendar' | 'leaf' | 'tiktok' | 'pinterest' }) {
  if (type === 'category') return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M5 6h6l2 3h6v9H5V6Z" /></svg>;
  if (type === 'clock') return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></svg>;
  if (type === 'calendar') return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M6 5h12v14H6V5Z" /><path d="M9 3v4M15 3v4M6 10h12" /></svg>;
  if (type === 'tiktok') return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M14 4v10.2a3.8 3.8 0 1 1-3.8-3.8" /><path d="M14 4c1 3 2.7 4.6 5 5" /></svg>;
  if (type === 'pinterest') return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8" /><path d="M11 18 13 9" /><path d="M9 11c0-2 1.5-3.2 3.4-3.2 2 0 3.4 1.3 3.4 3.2 0 2.1-1.2 3.8-3 3.8-.8 0-1.4-.4-1.6-1" /></svg>;
  return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M12 20c-1.3-4.7-.3-8.8 3-12" /><path d="M8 16c-2.5-2.4-3-5.5-1.5-9.3 3.2 1.1 5.2 3 6 5.7" /><path d="M14 13c2.7-.5 4.7-2.2 6-5-3.6-.5-6.2.2-7.8 2.2" /></svg>;
}

export function BlogDetailPanel({ slug, initialPost = null, initialRelatedPosts = [], preferInitialPost = false }: { slug: string; initialPost?: PlatformBlogPostDoc | null; initialRelatedPosts?: PlatformBlogPostDoc[]; preferInitialPost?: boolean }) {
  const { post: refreshedPost, loading, error } = usePublicBlogPost(slug, initialPost);
  const posts = initialRelatedPosts;
  const post = preferInitialPost ? initialPost ?? refreshedPost : refreshedPost ?? initialPost;

  if (!loading && !post) {
    return <div className="container mx-auto max-w-4xl px-4 py-16"><Card className="rounded-[2rem] border border-[#e7d5c7] bg-white shadow-sm"><CardContent className="p-8 text-sm text-[#6f6259]">This blog post does not exist.</CardContent></Card></div>;
  }

  const title = post?.title ?? 'Blog post';
  const excerpt = post?.excerpt ?? 'Post not found.';
  const bodySections = post?.body ? post.body.split(/\n{2,}/).map((item) => item.trim()).filter(Boolean) : [];
  const orderedBlocks = (post?.contentBlocks ?? []).filter((block) => block.text?.trim() || block.heading?.trim() || block.quote?.trim() || block.imageUrl?.trim());
  const hasOrderedBlocks = orderedBlocks.length > 0;
  const contentImages = post?.contentImages ?? [];
  const fullWidthImage = contentImages.find((image) => image.layout === 'full-width' && hasRenderableImage(image)) ?? null;
  const relatedReads = posts.filter((item) => item.slug !== post?.slug).slice(0, 3);
  const moreFromBlog = relatedReads.length ? relatedReads : posts.slice(0, 3);
  const tags = post ? visibleTags(post) : [];
  const featureInsertIndex = bodySections.length > 1 ? 1 : 0;
  const imageInsertIndex = bodySections.length > 2 ? 2 : featureInsertIndex;
  const dropCapIndex = Math.max(0, Number(post?.dropCapSectionIndex ?? 0));

  function articleUrl() {
    return typeof window !== 'undefined' ? window.location.href : '';
  }

  function copyArticleLink() {
    const url = articleUrl();
    if (url) void navigator.clipboard?.writeText(url);
  }

  function openShareDestination(destination: 'instagram' | 'tiktok' | 'pinterest' | 'email') {
    const url = articleUrl();
    copyArticleLink();
    if (typeof window === 'undefined') return;
    if (destination === 'email') {
      window.location.href = `mailto:?subject=${encodeURIComponent(post?.title || 'The Untangled Blog')}&body=${encodeURIComponent(url)}`;
      return;
    }
    if (destination === 'pinterest') {
      window.open(`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (destination === 'instagram') {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
      return;
    }
    window.open('https://www.tiktok.com/upload', '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="bg-[#fbf8f3] text-[#2f2d2a]">
      <section className="relative min-h-[560px] overflow-hidden border-b border-[#e7d5c7]">
        <div className={heroStyles.backdrop} data-scenery-hero>
          {post ? <CoverImageFill post={post} priority /> : <EmptyImageSurface />}
          <div className={heroStyles.overlay} />
        </div>
        <div className="container relative mx-auto flex min-h-[560px] max-w-[1340px] items-center px-6 py-10 sm:px-8 lg:px-2">
          <div className="w-full max-w-[540px] rounded-[1.45rem] border border-white/75 bg-[#fffaf6]/94 p-7 shadow-[0_24px_80px_rgba(70,49,35,.16)] backdrop-blur sm:p-9">
            <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#b8735c]">The Untangled Blog</p>
            <h1 className="mt-5 font-headline text-[2.8rem] leading-[1.02] tracking-tight text-[#2f2d2a] sm:text-[3.2rem]">{title}</h1>
            {post ? <p className="mt-5 text-sm text-[#6f6259]"><span className="font-semibold text-[#b8735c]">{visibleCategory(post)}</span><span className="px-3">-</span><span>{displayReadTime(post)} min read</span><span className="px-3">-</span><span>{postDateLabel(post)}</span></p> : null}
            <p className="mt-4 text-[15px] leading-7 text-[#4f4942]">{excerpt}</p>
          </div>
        </div>
      </section>

      <main className="container mx-auto max-w-[1340px] px-6 py-7 sm:px-8 lg:px-10">
        <nav className="mb-8 flex flex-wrap items-center gap-3 text-xs text-[#6f6259]">
          <Link href="/" className="hover:text-[#b8735c]">Home</Link><span>&gt;</span><Link href={ROUTES.blog} className="hover:text-[#b8735c]">The Blog</Link><span>&gt;</span><span className="text-[#2f2d2a]">{title}</span>
        </nav>

        <div className="grid gap-16 lg:grid-cols-[minmax(0,790px)_340px] lg:items-start xl:grid-cols-[minmax(0,820px)_360px] xl:gap-24">
          <article className="min-w-0 space-y-7">
            {loading && !post ? <p className="text-sm text-[#6f6259]">Loading article...</p> : null}
            {error ? <p className="text-sm text-[#6f6259]">Some article details could not be refreshed.</p> : null}
            {hasOrderedBlocks ? orderedBlocks.map((block, index) => (
              <ArticleContentBlock key={block.blockId || `${block.type}-${index}`} block={block} fallbackDropCap={index === 0 && post?.showDropCap !== false} />
            )) : bodySections.length ? bodySections.map((section, index) => {
              return (
                <div key={`${index}-${section.slice(0, 24)}`} className="space-y-7">
                  <ArticleSection section={section} dropCap={index === dropCapIndex && post?.showDropCap !== false} />
                  {index === featureInsertIndex && post ? <PullQuoteBox post={post} /> : null}
                  {index === featureInsertIndex && post ? <AccentCalloutBox post={post} /> : null}
                  {index === imageInsertIndex && fullWidthImage ? <ArticleImage image={fullWidthImage} /> : null}
                </div>
              );
            }) : <ArticleSection section={excerpt} dropCap={post?.showDropCap !== false} />}
            {!hasOrderedBlocks && !bodySections.length && fullWidthImage ? <ArticleImage image={fullWidthImage} /> : null}
            {!hasOrderedBlocks && post?.closingCallout?.trim() ? (
              <div className="grid grid-cols-[42px_minmax(0,1fr)] items-center gap-5 rounded-[.75rem] border border-[#ead8c8] bg-[#f7eee7] px-7 py-5 text-[15px] leading-7 text-[#5a4438]">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-[#b8735c]"><SmallIcon type="leaf" /></div>
                <p>{post.closingCallout.trim()}</p>
              </div>
            ) : null}

            {moreFromBlog.length ? (
              <section className="pt-6">
                <h2 className="font-headline text-3xl text-[#2f2d2a]">More from The Untangled Blog</h2>
                <div className="mt-6 grid gap-5 md:grid-cols-3">{moreFromBlog.map((item) => <ArticleSuggestionCard key={`more-${item.postId}`} post={item} />)}</div>
              </section>
            ) : null}
          </article>

          {post ? (
            <aside className="space-y-5 lg:sticky lg:top-24">
              {(post.authorName || post.authorBio || post.authorAvatarUrl) ? (
                <SidebarCard title="About the Author">
                  <div className="flex items-center gap-4">
                    <AuthorAvatar post={post} />
                    <div>
                      {post.authorName ? <p className="font-semibold text-[#2f2d2a]">{post.authorName}</p> : null}
                      {post.authorTitle ? <p className="mt-1 text-sm text-[#b8735c]">{post.authorTitle}</p> : null}
                    </div>
                  </div>
                  {post.authorBio ? <p className="mt-5 text-sm leading-7 text-[#5e554d]">{post.authorBio}</p> : null}
                  {post.authorProfileUrl ? <a href={post.authorProfileUrl} className="mt-4 inline-flex text-sm font-semibold text-[#b8735c] hover:text-[#7c4f3e]">More about the author -&gt;</a> : null}
                </SidebarCard>
              ) : null}

              <SidebarCard title="Article Details">
                <dl className="space-y-4 text-sm text-[#5e554d]">
                  <div className="grid grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-3"><dt className="text-[#6f6259]"><SmallIcon type="category" /></dt><dd>Category</dd><dd className="font-medium text-[#2f2d2a]">{visibleCategory(post)}</dd></div>
                  <div className="grid grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-3"><dt className="text-[#6f6259]"><SmallIcon type="clock" /></dt><dd>Reading time</dd><dd className="font-medium text-[#2f2d2a]">{displayReadTime(post)} min read</dd></div>
                  <div className="grid grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-3"><dt className="text-[#6f6259]"><SmallIcon type="calendar" /></dt><dd>Published</dd><dd className="font-medium text-[#2f2d2a]">{postDateLabel(post)}</dd></div>
                </dl>
              </SidebarCard>

              <SidebarCard title="Share & Save">
                <div className="flex flex-wrap gap-4">
                  <button type="button" title={`${post.likeCount || 0} likes`} className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e7d5c7] bg-[#fbf8f3] text-[#5e554d] hover:border-[#b8735c]"><ShareGlyph type="heart" /></button>
                  <button type="button" title={`${post.shareCount || 0} shares`} onClick={copyArticleLink} className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e7d5c7] bg-[#fbf8f3] text-[#5e554d] hover:border-[#b8735c]"><ShareGlyph type="link" /></button>
                  <SaveAccountItemButton
                    itemType="blogPost"
                    itemId={post.postId || post.slug}
                    title={post.title}
                    href={`${ROUTES.blog}/${post.slug}`}
                    description={post.excerpt || post.metaDescription}
                    imageUrl={post.coverImageUrl}
                    imageAlt={post.coverImageAlt || post.title}
                    className="h-12 rounded-full border-[#e7d5c7] bg-[#fbf8f3] text-[#5e554d] hover:border-[#b8735c]"
                  />
                </div>
                <p className="mt-5 text-xs font-semibold text-[#6f6259]">Share this article</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <button type="button" onClick={() => openShareDestination('instagram')} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e7d5c7] bg-[#fbf8f3] text-[#5e554d] hover:border-[#b8735c]"><ShareGlyph type="instagram" /></button>
                  <button type="button" onClick={() => openShareDestination('tiktok')} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e7d5c7] bg-[#fbf8f3] text-[#5e554d] hover:border-[#b8735c]"><SmallIcon type="tiktok" /></button>
                  <button type="button" onClick={() => openShareDestination('pinterest')} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e7d5c7] bg-[#fbf8f3] text-[#5e554d] hover:border-[#b8735c]"><SmallIcon type="pinterest" /></button>
                  <button type="button" onClick={() => openShareDestination('email')} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e7d5c7] bg-[#fbf8f3] text-[#5e554d] hover:border-[#b8735c]"><ShareGlyph type="mail" /></button>
                </div>
              </SidebarCard>

              {tags.length ? <SidebarCard title="Related Topics"><div className="flex flex-wrap gap-2">{tags.map((tag) => <Badge key={tag} variant="secondary" className="rounded-full border border-[#e5d3c5] bg-[#fbf7f1] px-3 py-1 text-[11px] font-semibold text-[#5f594f]">{tag}</Badge>)}</div></SidebarCard> : null}
              <BlogNewsletterBox compact />
              {relatedReads.length ? <SidebarCard title="Related Reads"><div className="space-y-2">{relatedReads.map((item) => <PopularRead key={`related-${item.postId}`} post={item} />)}</div><Button asChild variant="outline" className="mt-5 border-[#b8735c] bg-transparent text-[#8a5a45] hover:bg-[#fbf4ee]"><Link href={ROUTES.blog}>View all articles -&gt;</Link></Button></SidebarCard> : null}
            </aside>
          ) : null}
        </div>
      </main>
    </div>
  );
}
